// src/App.tsx
// Projectlys — aplicação de gestão de projetos (módulo único: management)

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { GlobalAuthProvider, useGlobalAuth } from './shared/contexts/GlobalAuthContext';
import LoginPage from './shared/pages/LoginPage';
import AdminPage from './shared/pages/AdminPage';
import { AuthProvider as QualityManagementAuthProvider } from './quality-management/contexts/AuthAdapter';
import { isManagementRole, isAdminRole } from './shared/lib/roles';
import {
  PMDashboardPage,
  EmployeeDashboardPage,
  EmployeeProjectView,
  OvertimeApprovalsPage,
  ProjectAnalyticsDashboard,
  TeamCalendarPage
} from './quality-management';
import DeliveryPlanPage from './quality-management/pages/DeliveryPlanPage';
import { ROUTES } from './shared/constants/routes';
import AppShell from './shared/components/AppShell';
import { LoadingScreen } from './shared/components/ui/Spinner';
import { ToastProvider } from './shared/components/ui/Toast';
import { AdminModalProvider } from './shared/components/AdminModal';

// Nova fundação visual (Tailwind + tokens). Deve vir antes do CSS legado.
import './shared/styles/tailwind.css';
import './shared/styles/tokens.css';
// CSS legado (transitório). legacy-compat.css foi REMOVIDO: redefinia utilities
// do Tailwind (.border, .rounded-lg, .shadow, .mb-*, .bg-white...) sem @layer,
// sequestrando o Tailwind e causando bordas claras/bg branco no dark.
import './shared/styles/variables.css';
import './shared/styles/design-system.css';
import './shared/styles/corporate-design.css';
import './App.css';
// Override final: corrige layout/tema das classes legadas (deve vir por último)
import './shared/styles/theme-overrides.css';

const App: React.FC = () => {
  React.useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (event.reason && (
        event.reason.message === 'Failed to fetch' ||
        event.reason.message === 'Unknown error' ||
        event.reason.status === 0
      )) {
        event.preventDefault();
        return;
      }
      console.error('Unhandled promise rejection:', {
        reason: event.reason,
        message: event.reason instanceof Error ? event.reason.message : 'Unknown error'
      });
      event.preventDefault();
    };

    const handleError = (event: ErrorEvent) => {
      console.error('Uncaught error:', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno
      });
      event.preventDefault();
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);

  return (
    <GlobalAuthProvider>
      <ToastProvider>
        <AppRoutes />
      </ToastProvider>
    </GlobalAuthProvider>
  );
};

// Roteia o usuário para o dashboard adequado conforme o papel.
// Usa a role real do backend (não a adaptada), senão director cairia no
// dashboard de funcionário.
const QualityManagementRouter: React.FC = () => {
  const { user } = useGlobalAuth();
  if (!isManagementRole(user?.role)) {
    return <Navigate to={ROUTES.QUALITY_MANAGEMENT.EMPLOYEE_DASHBOARD} replace />;
  }
  return <PMDashboardPage />;
};

const AppRoutes: React.FC = () => {
  const { loading, isAuthenticated } = useGlobalAuth();

  if (loading) {
    return <LoadingScreen message="Verificando autenticação..." />;
  }

  if (!isAuthenticated) {
    return (
      <Router>
        <Routes>
          <Route path="*" element={<LoginPage />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <AdminModalProvider>
        <QualityManagementAuthProvider>
          <Routes>
            {/* Shell persistente: a sidebar não remonta ao navegar (sem flash) */}
            <Route
              element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              <Route path={ROUTES.QUALITY_MANAGEMENT.BASE} element={<QualityManagementRouter />} />
              <Route
                path={ROUTES.QUALITY_MANAGEMENT.PM_DASHBOARD}
                element={<ManagementRoute><PMDashboardPage /></ManagementRoute>}
              />
              <Route path={ROUTES.QUALITY_MANAGEMENT.TEAM_CALENDAR} element={<TeamCalendarPage />} />
              <Route path={ROUTES.QUALITY_MANAGEMENT.EMPLOYEE_DASHBOARD} element={<EmployeeDashboardPage />} />
              <Route path={ROUTES.QUALITY_MANAGEMENT.PROJECT_DETAIL} element={<EmployeeProjectView />} />
              <Route
                path={ROUTES.QUALITY_MANAGEMENT.OVERTIME_APPROVALS}
                element={<ManagementRoute><OvertimeApprovalsPage /></ManagementRoute>}
              />
              <Route
                path={ROUTES.QUALITY_MANAGEMENT.DELIVERY_PLAN}
                element={<ManagementRoute><DeliveryPlanPage /></ManagementRoute>}
              />
              <Route
                path={ROUTES.QUALITY_MANAGEMENT.ANALYTICS_DASHBOARD}
                element={<ManagementRoute><ProjectAnalyticsDashboard /></ManagementRoute>}
              />

              {/* Admin: dentro do shell, com guarda de role */}
              <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
              <Route path="/admin/users" element={<AdminRoute><AdminPage initialTab="users" /></AdminRoute>} />
              <Route path="/admin/departments" element={<AdminRoute><AdminPage initialTab="departments" /></AdminRoute>} />
              <Route path={ROUTES.QUALITY_MANAGEMENT.ADMIN} element={<AdminRoute><AdminPage /></AdminRoute>} />
            </Route>

            {/* Default + fallback */}
            <Route path="/" element={<DefaultRoute />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </QualityManagementAuthProvider>
      </AdminModalProvider>
    </Router>
  );
};

// Rota protegida — apenas exige autenticação (app de módulo único)
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useGlobalAuth();
  if (!isAuthenticated) {
    return <Navigate to="/" />;
  }
  return <>{children}</>;
};

// Guarda de papel: páginas gerenciais não podem ser abertas por URL direta
// por um funcionário (antes o bloqueio existia só no menu lateral).
const ManagementRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useGlobalAuth();
  if (!isManagementRole(user?.role)) {
    return <Navigate to={ROUTES.QUALITY_MANAGEMENT.EMPLOYEE_DASHBOARD} replace />;
  }
  return <>{children}</>;
};

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useGlobalAuth();
  if (!isAdminRole(user?.role)) {
    return <Navigate to={ROUTES.QUALITY_MANAGEMENT.BASE} replace />;
  }
  return <>{children}</>;
};

// Rota padrão — redireciona para o management quando autenticado
const DefaultRoute: React.FC = () => {
  const { isAuthenticated } = useGlobalAuth();
  if (!isAuthenticated) {
    return <LoginPage />;
  }
  return <Navigate to={ROUTES.QUALITY_MANAGEMENT.BASE} replace />;
};

export default App;
